import { useQuery } from "@tanstack/react-query";
import type { ApiError, ConfiguracaoPublica, ContextoEuResposta, SessaoResposta } from "@zaphub/types";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

import { api, ErroApi, mensagemDeErro } from "../../lib/api";
import { contextoParaSessao, useSessao } from "../../lib/sessao";

/** Erro da API acompanhado do `Retry-After` (segundos) quando houve limite de tentativas (429). */
export class ErroComEspera extends ErroApi {
  constructor(
    status: number,
    code: string,
    mensagem: string,
    fields: Record<string, string[]> | undefined,
    readonly esperaSegundos: number | null,
  ) {
    super(status, code, mensagem, fields);
    this.name = "ErroComEspera";
  }
}

function lerRetryAfter(bruto: unknown): number | null {
  if (typeof bruto !== "string" && typeof bruto !== "number") return null;
  const n = Number(bruto);
  if (Number.isFinite(n) && n >= 0) return Math.ceil(n);
  const data = Date.parse(String(bruto));
  return Number.isNaN(data) ? null : Math.max(0, Math.ceil((data - Date.now()) / 1000));
}

/**
 * POST nas rotas `/auth/*`. Diferente do `api.post` puro, devolve o `Retry-After` do 429
 * (o interceptor global descarta os headers). Erros de rede continuam vindo como `ErroApi`.
 */
export async function postarAuth<T>(url: string, corpo?: unknown): Promise<T> {
  const r = await api.post<T | ApiError>(url, corpo, { validateStatus: () => true });
  if (r.status >= 200 && r.status < 300) return r.data as T;
  const erro: Partial<ApiError> = typeof r.data === "object" && r.data !== null ? (r.data as Partial<ApiError>) : {};
  throw new ErroComEspera(
    r.status,
    erro.code ?? "erro",
    erro.message ?? "Erro inesperado. Tente novamente.",
    erro.fields,
    r.status === 429 ? lerRetryAfter((r.headers as Record<string, unknown> | undefined)?.["retry-after"]) : null,
  );
}

/** `GET /publico/configuracao` (duração do trial e Client ID do Google). Cache compartilhado entre Entrar e Cadastro. */
export function useConfiguracaoPublica() {
  return useQuery({
    queryKey: ["publico", "configuracao"],
    queryFn: async () => (await api.get<ConfiguracaoPublica>("/publico/configuracao")).data,
    staleTime: 5 * 60_000,
  });
}

/** Rotas internas seguras para voltar depois de entrar (nunca URL externa nem telas de entrada). */
function rotaInternaSegura(de: unknown): string | null {
  if (typeof de !== "string" || !de.startsWith("/") || de.startsWith("//")) return null;
  if (/^\/(entrar|cadastro|esqueci-senha|redefinir-senha)(\/|$|\?)/.test(de)) return null;
  return de;
}

/** Para onde ir depois de entrar/cadastrar, conforme papel e assinatura. */
export function destinoPosEntrada(de?: unknown): string {
  const { usuario, conta } = useSessao.getState();
  const volta = rotaInternaSegura(de);
  if (usuario?.role === "SUPERADMIN") return volta?.startsWith("/plataforma") ? volta : "/plataforma";
  if (conta && !conta.assinaturaAtiva) return "/cobranca/assinatura-necessaria";
  return volta && !volta.startsWith("/plataforma") ? volta : "/";
}

/**
 * Guarda o access token (só em memória), busca o contexto em `/auth/eu` e preenche a sessão.
 * Se o contexto falhar, desfaz o token para não deixar meia-sessão.
 */
export async function iniciarSessao(resposta: SessaoResposta): Promise<void> {
  const { definirToken, definirSessao, limpar } = useSessao.getState();
  definirToken(resposta.accessToken);
  try {
    const { data } = await api.get<ContextoEuResposta>("/auth/eu");
    definirSessao(contextoParaSessao(data));
  } catch (erro) {
    limpar();
    throw erro;
  }
}

/**
 * Mapeia `ErroApi.fields` (400) para os campos do formulário. Devolve a mensagem geral
 * quando o erro não é de campo (ou nenhum campo conhecido veio), senão `null`.
 */
export function aplicarErroApi<T extends FieldValues>(
  erro: unknown,
  setError: UseFormSetError<T>,
  campos: readonly Path<T>[],
): string | null {
  if (erro instanceof ErroApi && erro.fields) {
    let mapeou = false;
    for (const campo of campos) {
      const mensagem = erro.fields[campo]?.[0];
      if (mensagem) {
        setError(campo, { type: "server", message: mensagem });
        mapeou = true;
      }
    }
    if (mapeou) return null;
  }
  return mensagemDeErro(erro);
}
